import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import { http } from "@/lib/api";

/**
 * Sprint 60.30 — Forgot Password page. Enumeration-safe:
 * backend always returns 200 whether the email exists or not,
 * so we render the same success screen regardless.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await http.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many reset attempts. Try again in an hour.");
      } else {
        // Fail-open: still show success to avoid enumeration.
        setSent(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md border border-zinc-800 bg-zinc-950/80 backdrop-blur p-8">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] mb-6"
          data-testid="back-to-login-link"
        >
          <ArrowLeft size={12} /> Back to login
        </Link>
        <h1 className="text-2xl font-bold mb-1">Reset your password</h1>
        <p className="text-sm text-zinc-400 mb-6">
          {sent
            ? "Check your inbox. If an account exists for that email, we've sent a link valid for 60 minutes."
            : "Enter the email you used to sign up. We'll send you a link to reset your password."}
        </p>

        {!sent ? (
          <form onSubmit={onSubmit} data-testid="forgot-password-form" className="space-y-4">
            <div>
              <label className="text-[11px] mono uppercase tracking-widest text-zinc-500 mb-1.5 block">
                Email
              </label>
              <div className="relative">
                <EnvelopeSimple
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@warpaintdfs.com"
                  data-testid="forgot-email-input"
                  className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none pl-10 pr-3 py-2.5 text-sm text-zinc-100"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy || !email.trim()}
              data-testid="forgot-submit-btn"
              className="w-full bg-[#39FF14] hover:bg-[#2fd811] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold py-3 uppercase tracking-widest text-xs transition-colors"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        ) : (
          <div
            data-testid="forgot-sent-confirmation"
            className="border border-[#39FF14]/50 bg-[#39FF14]/10 p-4"
          >
            <div className="text-[11px] mono uppercase tracking-widest text-[#39FF14] mb-2">
              Sent
            </div>
            <p className="text-sm text-zinc-300">
              Link sent to <strong>{email}</strong>. Didn't get it? Check spam, or{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                data-testid="resend-link-btn"
                className="text-[#39FF14] hover:underline"
              >
                try again
              </button>
              .
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
